/**
 * User profile lifecycle (PRD §37).
 *
 * `buildNewProfile` is pure so profile creation can be unit tested without
 * a backend; everything else delegates to the resolved backend.
 */

import { getBackend } from "../backend";
import { aliasFromDisplayName, generateAlias } from "../../utils/alias";

/** The shape written to `users/{uid}` on first sign-in. */
export function buildNewProfile(authUser, { now = Date.now, alias } = {}) {
  if (!authUser?.uid) {
    throw new Error("buildNewProfile requires an authenticated user");
  }

  const timestamp = now();
  const isGuest = Boolean(authUser.isAnonymous);

  return {
    uid: authUser.uid,
    alias:
      alias ??
      (isGuest ? generateAlias() : aliasFromDisplayName(authUser.displayName)),
    displayName: authUser.displayName ?? null,
    isGuest,
    footballIQ: 0,
    gamesWatched: 0,
    conceptsLearned: 0,
    predictionsCorrect: 0,
    createdAt: timestamp,
    lastActiveAt: timestamp,
  };
}

/**
 * Fields that must be refreshed when a guest converts to a real account.
 * The alias is deliberately preserved: it is how the user's friends know
 * them on the leaderboard.
 */
export function buildConversionPatch(authUser, { now = Date.now } = {}) {
  return {
    isGuest: Boolean(authUser.isAnonymous),
    displayName: authUser.displayName ?? null,
    lastActiveAt: now(),
  };
}

/** Read the profile, creating it on first sign-in. */
export async function ensureUserProfile(authUser) {
  const backend = getBackend();
  if (!backend) throw new Error("No backend available");

  const existing = await backend.getUserProfile(authUser.uid);

  if (!existing) {
    return backend.createUserProfile(buildNewProfile(authUser));
  }

  // Keep isGuest/displayName honest after a guest → Google conversion.
  const patch = buildConversionPatch(authUser);
  const needsUpdate =
    existing.isGuest !== patch.isGuest ||
    (existing.displayName ?? null) !== patch.displayName;

  if (needsUpdate) {
    await backend.updateUserProfile(authUser.uid, patch);
    return { ...existing, ...patch };
  }

  return existing;
}

export async function updateAlias(uid, alias) {
  const trimmed = String(alias ?? "").trim().slice(0, 24);
  if (trimmed.length < 2) {
    throw new Error("Alias must be at least 2 characters");
  }
  await getBackend().updateUserProfile(uid, { alias: trimmed });
  return trimmed;
}

export async function touchLastActive(uid) {
  try {
    await getBackend().updateUserProfile(uid, { lastActiveAt: Date.now() });
  } catch {
    /* a failed heartbeat must never break the session */
  }
}

export function subscribeToProfile(uid, callback) {
  return getBackend().subscribeUserProfile(uid, callback);
}
